import { useState, useEffect, useRef, useMemo, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIssuers, useCreateIssuer, useEditIssuer, useVerifyIssuer, useDeleteIssuer, useBadges, useUsage, useIssuerSettings, useUpdateIssuerSettings } from '../hooks/useApi';
import type { Badge, Issuer, IssuerSettings } from '../types';
import { LimitWarning, isAtLimit } from '../components/LimitWarning';
import { ActionErrorPanel } from '../components/ActionErrorPanel';

// Client-side format guards mirroring the backend validators, so a bad value is
// caught inline instead of round-tripping to a 400 (which the error reporter
// would otherwise file as an exception ticket). The issuer URL must be a full
// http(s) URL with a dotted host; the brand color must be a 6-digit hex.
function isValidWebsiteUrl(u: string): boolean {
  try {
    const p = new URL(u);
    return (p.protocol === 'http:' || p.protocol === 'https:') && /\.[a-z]{2,}$/i.test(p.hostname);
  } catch {
    return false;
  }
}
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

type ModalState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; issuer: Issuer }
  | { kind: 'verify'; issuer: Issuer }
  | { kind: 'settings'; issuer: Issuer }
  | { kind: 'delete'; issuer: Issuer };

/* ---------- Modal backdrop ---------- */
const OVERLAY_WIDTHS = { md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-xl', '2xl': 'max-w-2xl' } as const;
function Overlay({ children, onClose, size = 'md' }: { children: React.ReactNode; onClose: () => void; size?: keyof typeof OVERLAY_WIDTHS }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative z-10 w-full ${OVERLAY_WIDTHS[size]} max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl`}>
        {children}
      </div>
    </div>
  );
}

/* ---------- Create Issuer modal ---------- */
function CreateIssuerModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('dashboard');
  const { mutate: createIssuer, isPending } = useCreateIssuer();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [url, setUrl] = useState('');
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [linkedinOrgId, setLinkedinOrgId] = useState('');
  const [error, setError] = useState('');
  const [serverError, setServerError] = useState<Error | null>(null);
  const [lastPayload, setLastPayload] = useState<Record<string, unknown> | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => { nameRef.current?.focus(); }, []);

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) { setLogo(undefined); return; }
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result as string);
    reader.readAsDataURL(file);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(''); setServerError(null);
    if (name.trim().length < 3) { setError('Name must be at least 3 characters.'); return; }
    if (!email.trim()) { setError('Email is required.'); return; }
    if (!url.trim()) { setError('URL is required.'); return; }
    if (!isValidWebsiteUrl(url.trim())) { setError('Enter a valid website URL, e.g. https://example.com'); return; }
    if (linkedinOrgId.trim() && !/^\d{2,12}$/.test(linkedinOrgId.trim())) {
      setError('LinkedIn organization ID must be numeric.'); return;
    }
    const payload = {
      name: name.trim(), email: email.trim(), url: url.trim(), logo: logo ? '<image data omitted>' : undefined,
      linkedinOrganizationId: linkedinOrgId.trim() || undefined,
    };
    setLastPayload(payload);
    createIssuer(
      {
        name: name.trim(), email: email.trim(), url: url.trim(), logo,
        linkedinOrganizationId: linkedinOrgId.trim() || undefined,
      },
      { onSuccess: () => onClose(), onError: (err) => setServerError(err instanceof Error ? err : new Error(String(err))) }
    );
  }

  const inputCls = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400';
  const labelCls = 'block text-xs font-medium text-gray-700 mb-1';
  const helpCls = 'mt-1 text-[11px] text-gray-500 leading-relaxed';

  return (
    <Overlay onClose={onClose} size="2xl">
      <h3 className="text-lg font-semibold text-gray-900">{t('issuerForm.newTitle')}</h3>
      <p className="mt-1 mb-5 text-sm text-gray-500">{t('issuerForm.newSubtitle')}</p>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Identity — two columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
          <div>
            <label className={labelCls}>{t('issuerForm.name')}</label>
            <input ref={nameRef} type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t('issuers.namePlaceholder', 'Organization name')} className={inputCls} />
            <p className={helpCls}>{t('issuerForm.nameHelp')}</p>
          </div>
          <div>
            <label className={labelCls}>{t('issuerForm.email')}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@example.com" className={inputCls} />
            <p className={helpCls}>{t('issuerForm.emailHelp')}</p>
          </div>
          <div>
            <label className={labelCls}>{t('issuerForm.url')}</label>
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com" className={inputCls} />
            <p className={helpCls}>{t('issuerForm.urlHelp')}</p>
          </div>
          <div>
            <label className={labelCls}>{t('issuerForm.logo')} <span className="font-normal text-gray-400">{t('issuerForm.optional')}</span></label>
            <div className="flex items-center gap-3">
              {logo && <img src={logo} alt="Logo preview" className="h-11 w-11 flex-none rounded-lg object-cover border border-gray-200" />}
              <input type="file" accept="image/*" onChange={handleLogoChange}
                className="w-full text-sm text-gray-500 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-primary-700 hover:file:bg-primary-100" />
            </div>
            <p className={helpCls}>{t('issuerForm.logoHelp')}</p>
          </div>
        </div>

        {/* LinkedIn — full width so the guidance has room to breathe */}
        <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 items-start">
            <div>
              <label className={labelCls}>{t('issuerForm.linkedin')} <span className="font-normal text-gray-400">{t('issuerForm.optional')}</span></label>
              <input type="text" value={linkedinOrgId} onChange={(e) => setLinkedinOrgId(e.target.value.trim())}
                placeholder={t('issuers.linkedinOrgIdPlaceholder', 'e.g. 1035')} className={inputCls} inputMode="numeric" />
              <p className={helpCls}>{t('issuerForm.linkedinHelp')}</p>
            </div>
            <div className="rounded-lg bg-white border border-gray-200 px-3 py-2.5">
              <p className="text-[11px] font-semibold text-gray-700 mb-1.5">{t('issuerForm.whereToFind')}</p>
              <ol className="text-[11px] text-gray-500 leading-relaxed space-y-1 list-decimal pl-4">
                <li>{t('issuerForm.linkedinStep1')}</li>
                <li>{t('issuerForm.linkedinStep2')} <code className="rounded bg-gray-100 px-1 py-0.5 text-[10px]">linkedin.com/company/<strong>1035</strong>/admin/…</code></li>
                <li>{t('issuerForm.linkedinStep3Before')} <code className="rounded bg-gray-100 px-1 py-0.5 text-[10px]">/company/</code> {t('issuerForm.linkedinStep3And')} <code className="rounded bg-gray-100 px-1 py-0.5 text-[10px]">/admin/</code>.</li>
              </ol>
            </div>
          </div>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}
        <ActionErrorPanel
          error={serverError}
          context={{ resource: 'issuer', action: 'create', payload: lastPayload ?? undefined }}
          onDismiss={() => setServerError(null)}
        />
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">
            {t('issuerForm.cancel')}
          </button>
          <button type="submit" disabled={isPending}
            className="rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50">
            {isPending ? t('issuerForm.creating') : t('issuerForm.create')}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

/* ---------- Edit Issuer modal ---------- */
function EditIssuerModal({ issuer, onClose }: { issuer: Issuer; onClose: () => void }) {
  const { t } = useTranslation('dashboard');
  const { mutate: editIssuer, isPending } = useEditIssuer();
  const [name, setName] = useState(issuer.name);
  const [email, setEmail] = useState(issuer.email);
  const [url, setUrl] = useState(issuer.url);
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [logoChanged, setLogoChanged] = useState(false);
  const [linkedinOrgId, setLinkedinOrgId] = useState(issuer.linkedinOrganizationId || '');
  const [error, setError] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => { nameRef.current?.focus(); }, []);

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) { setLogo(undefined); setLogoChanged(false); return; }
    const reader = new FileReader();
    reader.onload = () => { setLogo(reader.result as string); setLogoChanged(true); };
    reader.readAsDataURL(file);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (name.trim().length < 3) { setError('Name must be at least 3 characters.'); return; }
    if (!email.trim()) { setError('Email is required.'); return; }
    if (!url.trim()) { setError('URL is required.'); return; }
    if (!isValidWebsiteUrl(url.trim())) { setError('Enter a valid website URL, e.g. https://example.com'); return; }
    if (linkedinOrgId.trim() && !/^\d{2,12}$/.test(linkedinOrgId.trim())) {
      setError('LinkedIn organization ID must be numeric.'); return;
    }
    editIssuer(
      {
        // Send the GUID in the path (full URL there breaks API Gateway routing
        // because %2F gets decoded before match). Backend's resolveIssuerId
        // tries current + legacy domains.
        issuerId: issuer.id.split('/').pop() || issuer.id,
        data: {
          name: name.trim(), email: email.trim(), url: url.trim(),
          logo: logoChanged ? logo : undefined,
          // Always send the field so the backend can clear it (empty string ⇒ removed).
          linkedinOrganizationId: linkedinOrgId.trim(),
        }
      },
      { onSuccess: () => onClose(), onError: (err) => setError(err.message) }
    );
  }

  const previewLogo = logo || issuer.image;
  const inputCls = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400';
  const labelCls = 'block text-xs font-medium text-gray-700 mb-1';
  const helpCls = 'mt-1 text-[11px] text-gray-500 leading-relaxed';

  return (
    <Overlay onClose={onClose} size="2xl">
      <h3 className="text-lg font-semibold text-gray-900">{t('issuerForm.editTitle')}</h3>
      <p className="mt-1 mb-5 text-sm text-gray-500">{t('issuerForm.editSubtitle')}</p>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Identity — two columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
          <div>
            <label className={labelCls}>{t('issuerForm.name')}</label>
            <input ref={nameRef} type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
            <p className={helpCls}>{t('issuerForm.nameHelp')}</p>
          </div>
          <div>
            <label className={labelCls}>{t('issuerForm.email')}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            <p className={helpCls}>{t('issuerForm.emailHelp')}</p>
          </div>
          <div>
            <label className={labelCls}>{t('issuerForm.url')}</label>
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} className={inputCls} />
            <p className={helpCls}>{t('issuerForm.urlHelp')}</p>
          </div>
          <div>
            <label className={labelCls}>{t('issuerForm.logo')}</label>
            <div className="flex items-center gap-3">
              {previewLogo && <img src={previewLogo} alt="Logo preview" className="h-11 w-11 flex-none rounded-lg object-cover border border-gray-200" />}
              <input type="file" accept="image/*" onChange={handleLogoChange}
                className="w-full text-sm text-gray-500 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-primary-700 hover:file:bg-primary-100" />
            </div>
            <p className={helpCls}>{t('issuerForm.logoHelp')}</p>
          </div>
        </div>

        {/* LinkedIn — full width so the guidance has room to breathe */}
        <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 items-start">
            <div>
              <label className={labelCls}>{t('issuerForm.linkedin')} <span className="font-normal text-gray-400">{t('issuerForm.optional')}</span></label>
              <input type="text" value={linkedinOrgId} onChange={(e) => setLinkedinOrgId(e.target.value.trim())}
                placeholder={t('issuers.linkedinOrgIdPlaceholder', 'e.g. 1035')} className={inputCls} inputMode="numeric" />
              <p className={helpCls}>{t('issuerForm.linkedinHelpEdit')}</p>
            </div>
            <div className="rounded-lg bg-white border border-gray-200 px-3 py-2.5">
              <p className="text-[11px] font-semibold text-gray-700 mb-1.5">{t('issuerForm.whereToFind')}</p>
              <ol className="text-[11px] text-gray-500 leading-relaxed space-y-1 list-decimal pl-4">
                <li>{t('issuerForm.linkedinStep1')}</li>
                <li>{t('issuerForm.linkedinStep2')} <code className="rounded bg-gray-100 px-1 py-0.5 text-[10px]">linkedin.com/company/<strong>1035</strong>/admin/…</code></li>
                <li>{t('issuerForm.linkedinStep3Before')} <code className="rounded bg-gray-100 px-1 py-0.5 text-[10px]">/company/</code> {t('issuerForm.linkedinStep3And')} <code className="rounded bg-gray-100 px-1 py-0.5 text-[10px]">/admin/</code>.</li>
              </ol>
            </div>
          </div>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">
            {t('issuerForm.cancel')}
          </button>
          <button type="submit" disabled={isPending}
            className="rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50">
            {isPending ? t('issuerForm.saving') : t('issuerForm.save')}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

/* ---------- Verify Issuer modal ---------- */
function VerifyIssuerModal({ issuer, onClose }: { issuer: Issuer; onClose: () => void }) {
  const { mutate: verifyIssuer, isPending } = useVerifyIssuer();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const trimmed = code.trim();
    if (trimmed.length !== 5) { setError('Code must be exactly 5 characters.'); return; }
    verifyIssuer(
      { issuerId: issuer.id.split('/').pop() || issuer.id, code: trimmed },
      { onSuccess: () => onClose(), onError: (err) => setError(err.message) }
    );
  }

  return (
    <Overlay onClose={onClose}>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Verify Issuer</h3>
      <p className="text-sm text-gray-500 mb-4">
        Enter the 5-letter verification code sent to <span className="font-medium text-gray-700">{issuer.email}</span>.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          ref={inputRef}
          type="text"
          maxLength={5}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABCDE"
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-center text-lg font-mono tracking-widest text-gray-900 placeholder:text-gray-300 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending || code.trim().length !== 5}
            className="rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50"
          >
            {isPending ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

/* ---------- Delete Issuer modal ---------- */
function DeleteIssuerModal({ issuer, linkedBadgeCount, onClose }: { issuer: Issuer; linkedBadgeCount: number; onClose: () => void }) {
  const { mutate: deleteIssuer, isPending } = useDeleteIssuer();
  const [error, setError] = useState('');
  const blocked = linkedBadgeCount > 0;

  function handleDelete() {
    if (blocked) return;
    setError('');
    deleteIssuer(issuer.id.split('/').pop() || issuer.id, {
      onSuccess: () => onClose(),
      onError: (err) => setError(err.message),
    });
  }

  return (
    <Overlay onClose={onClose}>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Issuer</h3>
      {linkedBadgeCount > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <p className="font-semibold mb-1">Cannot delete</p>
          <p>
            <span className="font-semibold">{issuer.name}</span> is linked to <span className="font-semibold">{linkedBadgeCount}</span> badge{linkedBadgeCount === 1 ? '' : 's'}. Delete those badge{linkedBadgeCount === 1 ? '' : 's'} first, then come back to delete the issuer.
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          Are you sure you want to delete <span className="font-semibold text-gray-700">{issuer.name}</span>? This action cannot be undone.
        </p>
      )}
      {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      <div className="mt-5 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          {blocked ? 'Close' : 'Cancel'}
        </button>
        {!blocked && (
          <button
            onClick={handleDelete}
            disabled={isPending}
            className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-red-700 active:scale-[0.98] disabled:opacity-50"
          >
            {isPending ? 'Deleting...' : 'Delete'}
          </button>
        )}
      </div>
    </Overlay>
  );
}

/* ---------- Issuer branding & delivery settings modal ---------- */
function IssuerSettingsModal({ issuer, onClose }: { issuer: Issuer; onClose: () => void }) {
  const { t } = useTranslation('dashboard');
  const guid = issuer.id.split('/').pop() || issuer.id;
  const { data: settings, isLoading } = useIssuerSettings(guid);
  const { mutate: save, isPending } = useUpdateIssuerSettings();

  const [form, setForm] = useState<IssuerSettings>({});
  const [error, setError] = useState<Error | null>(null);
  const [saved, setSaved] = useState(false);
  const loadedRef = useRef(false);

  // Seed the form once, when settings first arrive.
  useEffect(() => {
    if (settings && !loadedRef.current) {
      setForm({
        emailFromName: settings.emailFromName ?? '',
        replyTo: settings.replyTo ?? '',
        brandColor: settings.brandColor ?? '',
      });
      loadedRef.current = true;
    }
  }, [settings]);

  function set<K extends keyof IssuerSettings>(key: K, value: IssuerSettings[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  }

  function handleSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const bc = (form.brandColor ?? '').trim();
    if (bc && !HEX_COLOR_RE.test(bc)) {
      setError(new Error('Brand color must be a hex color like #7c3aed.'));
      return;
    }
    save(
      { issuerId: guid, patch: form },
      {
        onSuccess: () => { setSaved(true); },
        onError: (err: unknown) => setError(err instanceof Error ? err : new Error(String(err))),
      }
    );
  }

  const labelCls = 'block text-xs font-medium text-gray-700 mb-1';
  const inputCls = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400';

  return (
    <Overlay onClose={onClose}>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('issuerSettings.title')}</h3>
      <p className="text-xs text-gray-500 mb-4">{t('issuerSettings.subtitleShort', { name: issuer.name })}</p>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-9 rounded-lg bg-gray-100 animate-pulse" />)}</div>
      ) : (
        <form onSubmit={handleSave} className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
          {/* Branding */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">{t('issuerSettings.brandingSection')}</p>
            <div>
              <label className={labelCls}>{t('issuerSettings.brandColor')}</label>
              <div className="flex items-center gap-2">
                <input type="color" className="h-9 w-12 rounded border border-gray-200 bg-white p-0.5" value={/^#[0-9a-fA-F]{6}$/.test(form.brandColor ?? '') ? form.brandColor : '#7c3aed'} onChange={(e) => set('brandColor', e.target.value)} />
                <input type="text" className={inputCls} placeholder="#7c3aed" value={form.brandColor ?? ''} onChange={(e) => set('brandColor', e.target.value)} />
              </div>
              <p className="mt-1 text-[11px] text-gray-500">{t('issuerSettings.brandColorHelp')}</p>
            </div>
          </div>

          {/* Email delivery */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">{t('issuerSettings.emailSection')}</p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>{t('issuerSettings.emailFromName')}</label>
                <input type="text" maxLength={78} className={inputCls} placeholder={issuer.name} value={form.emailFromName ?? ''} onChange={(e) => set('emailFromName', e.target.value)} />
                <p className="mt-1 text-[11px] text-gray-500">{t('issuerSettings.emailFromNameHelp')}</p>
              </div>
              <div>
                <label className={labelCls}>{t('issuerSettings.replyTo')}</label>
                <input type="email" className={inputCls} placeholder="support@yourdomain.com" value={form.replyTo ?? ''} onChange={(e) => set('replyTo', e.target.value)} />
                <p className="mt-1 text-[11px] text-gray-500">{t('issuerSettings.replyToHelp')}</p>
              </div>
            </div>
          </div>

          <ActionErrorPanel
            error={error}
            context={{ resource: 'issuer', action: 'update', payload: form as Record<string, unknown> }}
            onDismiss={() => setError(null)}
          />
          {saved && !error && <p className="text-xs font-medium text-emerald-600">{t('issuerSettings.saved')}</p>}

          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={isPending} className="rounded-lg bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors">
              {isPending ? t('issuerSettings.saving') : t('issuerSettings.save')}
            </button>
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors">
              {t('issuerSettings.close')}
            </button>
          </div>
        </form>
      )}
    </Overlay>
  );
}

/* ---------- Issuer card ---------- */
function IssuerCard({
  issuer,
  badgeCount,
  onEdit,
  onVerify,
  onSettings,
  onDelete,
}: {
  issuer: Issuer;
  badgeCount: number;
  onEdit: () => void;
  onVerify: () => void;
  onSettings: () => void;
  onDelete: () => void;
}) {
  const guid = issuer.id.split('/').pop() || issuer.id;
  const linkedinUrl = issuer.linkedinOrganizationId
    ? `https://www.linkedin.com/company/${issuer.linkedinOrganizationId}/`
    : null;
  const initial = (issuer.name?.charAt(0) || '?').toUpperCase();
  const cleanUrl = (issuer.url || '').replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <div className="group rounded-2xl border border-gray-200 bg-white shadow-card overflow-hidden transition-all hover:shadow-md hover:border-gray-300 flex flex-col">
      {/* Gradient banner with verified pill */}
      <div className="relative h-20 bg-gradient-to-br from-primary-500 via-primary-600 to-violet-600">
        {issuer.verified ? (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 shadow-sm backdrop-blur-sm">
            <svg className="h-3 w-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
            </svg>
            Verified
          </span>
        ) : (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800 shadow-sm border border-amber-200">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            Unverified
          </span>
        )}
      </div>

      {/* Avatar — floats over the banner edge. `relative` is required so
          the avatar joins the same stacking context as the banner above
          (which is also `relative`) and paints on top as a later sibling. */}
      <div className="relative px-6 -mt-9">
        {issuer.image ? (
          <img
            src={issuer.image}
            alt={`${issuer.name} logo`}
            className="h-16 w-16 rounded-2xl object-cover border-4 border-white shadow-md bg-white"
          />
        ) : (
          <div className="h-16 w-16 rounded-2xl border-4 border-white shadow-md bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center text-2xl font-bold text-primary-700">
            {initial}
          </div>
        )}
      </div>

      {/* Name + URL — own row, below the banner */}
      <div className="px-6 pt-3 min-w-0">
        <h3 className="text-lg font-bold text-gray-900 truncate" title={issuer.name}>{issuer.name}</h3>
        {cleanUrl && (
          <a
            href={issuer.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:text-primary-600 hover:underline inline-flex items-center gap-1 truncate max-w-full"
            title={issuer.url}
          >
            <span className="truncate">{cleanUrl}</span>
            <svg className="h-3 w-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </div>

      {/* Body */}
      <div className="px-6 py-4 space-y-3 flex-1">
        {/* Metric strip: badge count + LinkedIn pill (when set) */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-1.5">
            <svg className="h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H9.414a1 1 0 00-.707.293L3.293 8.707A1 1 0 003 9.414V19a2 2 0 002 2z" />
            </svg>
            <span className="text-xs font-bold text-gray-900">{badgeCount}</span>
            <span className="text-xs text-gray-500">linked badge{badgeCount === 1 ? '' : 's'}</span>
          </div>
          {linkedinUrl && (
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#0a66c2]/5 border border-[#0a66c2]/20 px-3 py-1.5 text-xs font-medium text-[#0a66c2] hover:bg-[#0a66c2]/10 transition-colors"
              title={`Open linkedin.com/company/${issuer.linkedinOrganizationId}`}
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              {issuer.linkedinOrganizationId}
            </a>
          )}
        </div>

        {/* Email */}
        <a
          href={`mailto:${issuer.email}`}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600 hover:underline group/email"
        >
          <svg className="h-4 w-4 flex-shrink-0 text-gray-400 group-hover/email:text-primary-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
          </svg>
          <span className="truncate">{issuer.email}</span>
        </a>
      </div>

      {/* Footer: GUID + actions */}
      <div className="px-6 py-3 bg-gray-50/60 border-t border-gray-100 flex items-center justify-between gap-3">
        <span
          className="font-mono text-[10px] text-gray-400 truncate min-w-0"
          title={issuer.id}
        >
          {guid}
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!issuer.verified && (
            <>
              <button
                onClick={onVerify}
                className="rounded-lg bg-primary-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-primary-700 transition-colors"
              >
                Verify
              </button>
              <button
                onClick={onEdit}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Edit
              </button>
            </>
          )}
          <button
            onClick={onSettings}
            className="rounded-lg border border-transparent p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 hover:border-gray-200 transition-colors"
            title="Branding & delivery"
            aria-label="Branding & delivery settings"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="rounded-lg border border-transparent p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
            title="Delete issuer"
            aria-label="Delete issuer"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Main page ---------- */
export function IssuersPage() {
  const { t } = useTranslation('dashboard');
  const { data: rawIssuers, isLoading } = useIssuers();
  const { data: rawBadges } = useBadges();
  const { data: usage } = useUsage();
  const atIssuerLimit = isAtLimit(usage, 'issuers');
  const issuers = rawIssuers ? (Array.isArray(rawIssuers) ? rawIssuers : ((rawIssuers as any).items ?? [])) : [];
  const badges = rawBadges ? (Array.isArray(rawBadges) ? rawBadges : ((rawBadges as any).items ?? [])) : [];
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });

  // Map of issuer.id → number of badges that reference it. Used to show badge
  // counts on cards and to gate the Delete modal with a clear explanation
  // before any backend round-trip.
  const badgesPerIssuer = useMemo(() => {
    const map = new Map<string, number>();
    for (const b of badges as Badge[]) {
      const issuerId = b.issuer?.id;
      if (!issuerId) continue;
      map.set(issuerId, (map.get(issuerId) ?? 0) + 1);
    }
    return map;
  }, [badges]);

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
      <div className="page-wide">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Issuers</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage the organizations that issue your badges.
            </p>
          </div>
          <button
            data-testid="new-issuer-button"
            onClick={() => setModal({ kind: 'create' })}
            disabled={atIssuerLimit}
            title={atIssuerLimit ? "You've reached the issuer limit on your plan" : undefined}
            className="rounded-xl bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
          >
            + {t('issuerForm.newTitle')}
          </button>
        </div>

        <LimitWarning usage={usage} resource="issuers" />

        {/* Next-step nudge: a verified issuer with no badge yet is the single
            biggest new-user stall (2026-10 activation audit — users verify an
            issuer then never reach the designer). Point them straight at it. */}
        {issuers.some((i: Issuer) => i.verified) && badges.length === 0 && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary-200 bg-primary-50 px-5 py-4">
            <p className="text-sm text-primary-900">
              <span className="font-semibold">Issuer verified 🎉</span> — now design your first badge so you can start issuing credentials.
            </p>
            <Link
              to="/badges"
              className="flex-shrink-0 rounded-lg bg-primary-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98]"
            >
              Design a badge →
            </Link>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="grid gap-5 sm:grid-cols-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-44 rounded-2xl border border-gray-200 bg-white animate-pulse" />
            ))}
          </div>
        ) : issuers && issuers.length > 0 ? (
          <div className="grid gap-5 sm:grid-cols-2">
            {issuers.map((issuer: Issuer) => (
              <IssuerCard
                key={issuer.id}
                issuer={issuer}
                badgeCount={badgesPerIssuer.get(issuer.id) ?? 0}
                onEdit={() => setModal({ kind: 'edit', issuer })}
                onVerify={() => setModal({ kind: 'verify', issuer })}
                onSettings={() => setModal({ kind: 'settings', issuer })}
                onDelete={() => setModal({ kind: 'delete', issuer })}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-card">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0z" />
              </svg>
            </div>
            <p className="text-sm text-gray-500">No issuers yet. Create your first issuer to start issuing badges.</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {modal.kind === 'create' && (
        <CreateIssuerModal onClose={() => setModal({ kind: 'closed' })} />
      )}
      {modal.kind === 'edit' && (
        <EditIssuerModal issuer={modal.issuer} onClose={() => setModal({ kind: 'closed' })} />
      )}
      {modal.kind === 'verify' && (
        <VerifyIssuerModal issuer={modal.issuer} onClose={() => setModal({ kind: 'closed' })} />
      )}
      {modal.kind === 'settings' && (
        <IssuerSettingsModal issuer={modal.issuer} onClose={() => setModal({ kind: 'closed' })} />
      )}
      {modal.kind === 'delete' && (
        <DeleteIssuerModal
          issuer={modal.issuer}
          linkedBadgeCount={badgesPerIssuer.get(modal.issuer.id) ?? 0}
          onClose={() => setModal({ kind: 'closed' })}
        />
      )}
    </div>
  );
}
